import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { StockService } from '../stock.service';
import { Stock } from '../stock.type';
import { SupplierProfile } from '../../supplier/supplier.type';
import { EmailLoginField } from '../../shared/components/email-login-field/email-login-field';
import { LoginInfo } from '../../login-info/login-info.type';

/**
 * Create or edit an account.
 *
 * There is no fee, status, balance or date field: the fee is the admin default
 * snapshotted server-side (overridden separately), the status moves only through
 * the lifecycle endpoints, and nothing about an account depends on a date.
 *
 * Login info is resolved (or created) through app-email-login-field, the same
 * component used everywhere else a login-info-backed email is picked — it
 * covers "no login info yet for this email" by opening the create dialog
 * inline, so a stock is never submitted against an email with no login info.
 */
@Component({
  selector: 'app-stock-form-dialog',
  imports: [
    CommonModule, FormsModule, ReactiveFormsModule, MatDialogModule, MatButtonModule,
    MatFormFieldModule, MatInputModule, MatSelectModule, MatProgressSpinnerModule,
    EmailLoginField,
  ],
  templateUrl: './stock-form-dialog.html',
  styleUrl: './stock-form-dialog.scss',
})
export class StockFormDialog {
  editing: boolean;
  emailControl = new FormControl<string>('', { nonNullable: true });
  selectedLoginInfo: LoginInfo | null = null;
  supplierId = '';
  coinsToBeRemain = 0;
  description = '';
  saving = false;
  error: string | null = null;

  constructor(
    public ref: MatDialogRef<StockFormDialog>,
    private service: StockService,
    @Inject(MAT_DIALOG_DATA) public data: { stock?: Stock; suppliers: SupplierProfile[] },
  ) {
    this.editing = Boolean(data.stock);
    if (data.stock) {
      this.emailControl.setValue(data.stock.login_info_email);
      this.description = data.stock.description ?? '';
    }
  }

  onLoginInfoChange(loginInfo: LoginInfo | null): void {
    this.selectedLoginInfo = loginInfo;
  }

  save(): void {
    this.saving = true;
    this.error = null;

    const done = () => { this.saving = false; this.ref.close(true); };
    const fail = (err: any) => {
      this.error = err?.error?.error ?? 'Failed to save';
      this.saving = false;
    };

    if (this.editing) {
      this.service.updateStock(this.data.stock!.id, {
        description: this.description,
      }).subscribe({ next: done, error: fail });
      return;
    }

    if (!this.selectedLoginInfo) {
      this.error = 'Add login info for this email first.';
      this.saving = false;
      return;
    }

    this.service.createStock({
      login_info_email: this.selectedLoginInfo.email,
      supplier_id: this.supplierId || undefined,
      coins_to_be_remain: this.coinsToBeRemain,
      description: this.description || undefined,
    }).subscribe({ next: done, error: fail });
  }
}
